import {Alert} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {MismatchStatus, SelectedSchemaRole} from './useSchemaMismatchDetection';
import type {SchemaMismatchResult} from './useSchemaMismatchDetection';

interface SchemaMismatchBannerProps {
  mismatchResult: SchemaMismatchResult;
}

/**
 * Error banner shown when the selected schema cannot create salt or solvate molecules but the
 * sketched structure includes salts or solvates.
 */
export function SchemaMismatchAlert({mismatchResult}: SchemaMismatchBannerProps): JSX.Element | null {
  if (mismatchResult.status !== MismatchStatus.BLOCKED) {
    return null;
  }

  return (
    <Alert
      variant="error"
      mb="md"
      icon={<BenchlingIcon icon="exclamation-circle" />}
      styles={{root: {flexShrink: 0}}}
      title="The schema you selected does not support the creation of salt or solvate molecules."
    >
      {mismatchResult.message}
    </Alert>
  );
}

/**
 * Info banner shown when a schema from a valid Form-Compound pair is selected to explain that the
 * created Molecule may use the other schema depending on whether salts or solvates are used.
 */
export function SchemaInfoBanner({mismatchResult}: SchemaMismatchBannerProps): JSX.Element | null {
  if (
    mismatchResult.status !== MismatchStatus.OK ||
    mismatchResult.linkedSchemaName == null ||
    mismatchResult.selectedSchemaRole == null
  ) {
    return null;
  }

  const isFormSchemaSelected = mismatchResult.selectedSchemaRole === SelectedSchemaRole.FORM;

  return (
    <Alert
      variant="info"
      mb="md"
      icon={<BenchlingIcon icon="info-circle" />}
      styles={{root: {flexShrink: 0}}}
      title="This schema supports the creation of both compound molecules and salted or solvate forms."
    >
      The molecule you draw will be created using this schema or its linked{' '}
      {isFormSchemaSelected ? 'Compound' : 'Form'} schema, {mismatchResult.linkedSchemaName}, if you
      {isFormSchemaSelected ? ' do not' : ''} include any salts or solvates.
    </Alert>
  );
}
